<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatMoney,
  rowDifferenceCents,
  rowValueCents,
} from '../composables/commissionSheetForm';

const props = defineProps({
  amountCents: { type: Number, default: 0 },
  row: { type: Object, required: true },
});

const { t } = useI18n();
const emptyValue = () => t('REAL_ESTATE_COMMISSION_INSTALLMENT.COMMON.EMPTY');
const groupLabel = () => {
  if (props.row.group === 'corretores_vendedores')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.VENDEDOR');
  if (props.row.group === 'corretores_opcionistas')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.OPCIONISTA');
  if (props.row.group === 'gerentes')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.GERENTE');
  if (props.row.group === 'gerentes_gerais')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.GERENTE_GERAL');
  if (props.row.group === 'diretores_gerais')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.DIRETOR_GERAL');
  if (props.row.group === 'repasse_franquias')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.REPASSE_FRANQUIA');
  if (props.row.group === 'royalties')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.ROYALTIES');
  if (props.row.group === 'comunicacao')
    return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.COMUNICACAO');
  return t('REAL_ESTATE_COMMISSION_INSTALLMENT.ROLES.EMPRESA');
};
const role = computed(() => `${props.row.index + 1}º ${groupLabel()}`);
const recipient = computed(
  () =>
    (props.row.tipo_pessoa === 'PJ'
      ? props.row.nome_empresarial
      : props.row.name) || emptyValue()
);
const document = computed(
  () =>
    (props.row.tipo_pessoa === 'PJ' ? props.row.cnpj : props.row.cpf) ||
    emptyValue()
);
const difference = computed(() => {
  const cents = rowDifferenceCents(props.row);
  if (!props.row.desconto && !props.row.bonificacao) return emptyValue();
  if (!cents) return formatMoney(0);
  return `${cents > 0 ? '+' : '-'} ${formatMoney(Math.abs(cents))}`;
});
const paymentParts = computed(() => {
  const values =
    props.row.tipo_pagamento === 'banco'
      ? [
          t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.BANK'),
          props.row.nome_banco,
          props.row.agencia,
          props.row.numero_conta,
        ]
      : [
          t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.PIX'),
          props.row.chave_pix,
          props.row.pix,
        ];
  return values.filter(Boolean);
});
</script>

<template>
  <tr class="bg-white">
    <td class="px-4 py-3 font-semibold text-lais-text">{{ role }}</td>
    <td class="px-4 py-3">
      {{
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.COMMON.PERCENTAGE', {
          value: row.porcentagem_real,
        })
      }}
    </td>
    <td class="px-4 py-3">{{ difference }}</td>
    <td class="px-4 py-3 font-semibold text-lais-primary">
      {{ formatMoney(rowValueCents(row, amountCents)) }}
    </td>
    <td class="px-4 py-3">{{ recipient }}</td>
    <td class="px-4 py-3">{{ document }}</td>
    <td class="px-4 py-3">{{ row.creci || emptyValue() }}</td>
    <td class="px-4 py-3">
      <div class="flex max-w-72 flex-wrap gap-1.5">
        <span
          v-for="(part, partIndex) in paymentParts"
          :key="`${row.group}-${row.index}-payment-${partIndex}`"
          class="rounded-full border border-lais-border-soft bg-lais-bg-soft px-2 py-1 text-xs"
          :class="
            partIndex === 0
              ? 'font-semibold text-emerald-700'
              : 'text-lais-text'
          "
        >
          {{ part }}
        </span>
      </div>
    </td>
  </tr>
</template>
